<template xmlns:v-slot="http://www.w3.org/1999/XSL/Transform">
    <v-menu v-model="menu_open" offset-y :close-on-content-click="false" :nudge-top="18" class="recurrence-menu">
        <template v-slot:activator="{ on }">
            <RecurrenceButton
                :menu-open="menu_open"
                :can-add-repeat="false"
                :current-state="current_state"
                :value="formatOutput(current_state)"
                :label="label"
                :error-messages="errorMessages"
                :on="on"
            />
            <div style="font-size: 12px !important" class="caption">{{ formatOutputHint(current_state) }}</div>
        </template>
        <v-card v-if="form_data">
            <v-card-text class="pt-2 pb-0">
                <v-row no-gutters>
                    <template v-if="pickerType !== 'workflow'">
                        <v-col
                            class="d-flex shrink"
                        >
                            <v-date-picker
                                :value="datesList"
                                min="1900-01-01"
                                color="primary"
                                no-title
                                :picker-date.sync="pickerDate"
                                class="elevation-0 date-picker-range"
                                range
                                flat
                                width="305"
                                :disabled="!startDate"
                                :locale="locale"
                                :events="coloring.events"
                                :event-color="coloring.colors"
                                :first-day-of-week="firstDayOfWeek"
                                @input="onChangeDatePicked"
                            />
                        </v-col>
                        <v-col class="shrink d-flex">
                            <div class="mx-3 pr-3 mt-12">
                                <v-divider
                                    vertical
                                />
                            </div>
                        </v-col>
                    </template>
                    <v-col
                        style="max-width: 350px"
                        class="mt-1"
                        :class="pickerType === 'workflow' ? 'pt-2' : 'pt-12'"
                    >
                        <v-row no-gutters>
                            <v-col class="col-6 px-1 py-0">
                                <v-text-field
                                    v-model="form_data.weeks_f"
                                    outlined
                                    class="task-form-elem"
                                    :error-messages="form_data.weeks_error"
                                    :label="$t('Week(s)')"
                                    @input="integer_field_changed('weeks', $event)"
                                ></v-text-field>
                            </v-col>
                            <v-col class="col-6 px-1 py-0">
                                <v-text-field
                                    v-model="form_data.days_f"
                                    outlined
                                    class="task-form-elem"
                                    :error-messages="form_data.days_error"
                                    :label="$t('Day(s)')"
                                    @input="integer_field_changed('days', $event)"
                                ></v-text-field>
                            </v-col>
                            <v-col class="col-6 px-1 py-0">
                                <v-select
                                    v-model="form_data.date_dir"
                                    :items="workflow_settings.date_dir_items"
                                    outlined
                                    :label="$t('Rule')"
                                    class="task-form-elem"
                                    :error-messages="form_data.date_dir_error"
                                    :menu-props="{ contentClass: 'select-dense-items' }"
                                ></v-select>
                            </v-col>
                            <v-col class="col-6 px-1 py-0">
                                <v-select
                                    v-model="form_data.date_field"
                                    :items="allowedDateFields"
                                    item-value="name"
                                    item-text="title"
                                    outlined
                                    :label="isEventTypeCompany(task.account_id) ? $t('Depends on event') : $t('Depends on project')"
                                    class="task-form-elem task-form-elem-select-nowrap"
                                    :error-messages="form_data.date_field_error"
                                    :menu-props="{ contentClass: 'select-dense-items' }"
                                ></v-select>
                            </v-col>
                            <v-col class="col-6 px-1 py-0 d-flex flex-nowrap">
                                <v-text-field
                                    v-model="form_data.due_count_days_f"
                                    outlined
                                    :label="$t('Due date in')"
                                    class="task-form-elem"
                                    :error-messages="form_data.due_count_days_error"
                                    @input="integer_field_changed('due_count_days', $event)"
                                ></v-text-field>
                                <div class="pt-2 ml-3 mt-5">{{ $t("days") }}</div>
                            </v-col>
                        </v-row>
                    </v-col>
                </v-row>
            </v-card-text>
            <v-card-actions class="dialog-actions-bar py-4 px-7">
                <v-spacer />
                <v-btn text @click="cancel">{{ $t("Cancel") }}</v-btn>
                <v-btn ref="saveButton" color="primary" class="inverted--text" @click="save">
                    {{ $t("Save") }}
                </v-btn>
            </v-card-actions>
        </v-card>
    </v-menu>
</template>

<script>
import Vue from "vue";
import {computed, onMounted, ref, watch} from "@vue/composition-api";
import {
    cleanNumber, formatRecurrence,
    formatRecurrenceWorkflow,
    formatSysDate,
    parseSysDate
} from "../../plugins/formatting";
import AppModule from "../../store/modules/app";
import { $t } from "@/i18n";
import errorDialog from "@/views/ErrorDialog/errorDialog"
import RecurrenceButton from "@/components/Task/RecurrenceField/RecurrenceButton.vue";
import ProjectModule from "@/store/modules/project";

export default {
    name: "WorkflowDateField",
    components: {RecurrenceButton},
    props: {
        label: {
            type: String,
            default: null
        },
        task: {
            type: Object,
            required: true
        },
        errorMessages: {
            type: String,
            default: null
        },
        pickerType: {
            type: String,
            default: "project"
        }
    },
    setup(props, ctx) {
        const { isEventTypeCompany } = ProjectModule();
        const { fetchClassifiers, custom_fields } = AppModule();

        const menu_open = ref(false);
        const form_data = ref(null);
        const opening = ref(true);
        const pickerDate = ref(null);
        const saveButton = ref(null);
        const workflow_settings = ref({
            date_dir_items: [{ value: "before", text: $t("Before") }, { value: "after", text: $t("After") }],
            date_fields: []
        })

        const locale = computed(function() {
            return Vue.formatting.culture;
        });
        const firstDayOfWeek = computed(function () {
            return Vue.formatting.first_week_day
        })

        const current_state = computed(function() {
            if (props.task.fields == null || !props.task.fields.workflow_date_rule) {
                return {};
            }
            return props.task.fields.workflow_date_rule;
        });

        const countDays = computed(() => {
            return (form_data.value.weeks == null ? 0 : form_data.value.weeks) * 7 + (form_data.value.days == null ? 0 : form_data.value.days);
        })

        const allowedDateFields = computed(() => {
            if (props.pickerType === "workflow") {
                return workflow_settings.value.date_fields
            } else {
                if (!props.task.project) {
                    return []
                }

                return workflow_settings.value.date_fields.filter(x => props.task.project[x.name])
            }
        })

        const load_data = async () => {
            let account_id = props.task.account_id;
            try {
                await fetchClassifiers({ types: ["custom_fields"], account_id })
                if (custom_fields.value[account_id] !== undefined) {
                    workflow_settings.value.date_fields = custom_fields.value[account_id]
                        .filter(x => x.related_type.startsWith("project.") && x.type === "date");
                }
            } catch (ex) {
                errorDialog.showError(ex)
            }

            let workflow_date_rule = null;

            if (props.task.fields == null || props.task.fields.workflow_date_rule == null) {
                workflow_date_rule = {
                    count_days: 0,
                    date_dir: "after",
                    date_field: null,
                    due_count_days: 0
                };

                if (!workflow_date_rule || !allowedDateFields.value.find(x => x.name === workflow_date_rule.date_field)) {
                    if (allowedDateFields.value.find(x => x.name === 'start_date')) {
                        workflow_date_rule.date_field = 'start_date'
                    } else if (allowedDateFields.value.length) {
                        workflow_date_rule.date_field = allowedDateFields.value[0].name
                    } else {
                        workflow_date_rule.date_field = null
                    }
                }
            }
            else {
                workflow_date_rule = Vue.clone_object(props.task.fields.workflow_date_rule);
            }

            if (workflow_date_rule && !allowedDateFields.value.find(x => x.name === workflow_date_rule.date_field)) {
                workflow_date_rule.date_field = null
            }

            workflow_date_rule.days = workflow_date_rule.count_days % 7;
            workflow_date_rule.weeks = (workflow_date_rule.count_days - workflow_date_rule.days) / 7;

            workflow_date_rule = Vue.prepare_form_data(workflow_date_rule, {
                count_days: "integer",
                days: "integer",
                weeks: "integer",
                date_dir: "choice_single",
                date_field: "text",
                due_count_days: "integer"
            });

            form_data.value = workflow_date_rule;
            if (!(props.task.fields && props.task.fields.workflow_date_rule)) {
                if (isWfValid()) {
                    save({})
                }
            }

            focusSave();
        }
        const focusSave = () => {
            setTimeout(function() {
                opening.value = false;
                if (saveButton.value != null) saveButton.value.$el.focus();
            }, 100);
        }
        const isWfValid = () => {
            let valid = true;
            if (form_data.value.weeks == null && form_data.value.days == null) {
                valid = false;
            }
            if (!form_data.value.date_dir) {
                valid = false;
            }
            if (!form_data.value.date_field) {
                valid = false;
            }
            return valid;
        };
        const validateWf = () => {
            let valid = true;
            if (form_data.value.weeks == null && form_data.value.days == null) {
                form_data.value.days_error = $t("Required field");
                valid = false;
            }
            if (!form_data.value.date_dir) {
                form_data.value.date_dir_error = $t("Required field");
                valid = false;
            }
            if (!form_data.value.date_field) {
                form_data.value.date_field_error = $t("Required field");
                valid = false;
            }
            return valid;
        };
        const get_clean_value = () => {
            let count_days = (form_data.value.weeks == null ? 0 : form_data.value.weeks) * 7 + (form_data.value.days == null ? 0 : form_data.value.days);
            return {
                count_days: count_days,
                date_dir: form_data.value.date_dir ? form_data.value.date_dir : "before",
                date_field: form_data.value.date_field,
                has_due_date: form_data.value.due_count_days > 0 ? 1 : 0,
                due_count_days: form_data.value.due_count_days == null ? 0 : form_data.value.due_count_days
            };
        };
        const save = (e) => {
            if (!validateWf()) return;

            ctx.emit("change", get_clean_value());
            menu_open.value = false;

            // Assume enter is pressed
            if (e.clientX === 0 && e.clientY === 0) {
                ctx.emit("tab_next");
            }
        };
        const cancel = () => {
            menu_open.value = false;
            ctx.emit("tab_next");
        };
        const formatOutputHint = (val) => {
            if (props.pickerType === 'workflow') {
                return null
            }

            return formatRecurrenceWorkflow(val, custom_fields.value[props.task.account_id]);
        };

        const projectDate = computed(() => {
            if (!form_data.value) return null;
            if (!form_data.value.date_field) return null;
            if (!props.task.project) return null;
            return formatSysDate(parseSysDate(props.task.project[form_data.value.date_field]));
        })

        const startDate = computed(() => {
            if (props.pickerType === "workflow" || !projectDate.value) {
                return null
            }

            let date_val = props.task.project[form_data.value.date_field];
            if (!date_val) return null;
            let d = parseSysDate(date_val).calcAddDays(
                (!countDays.value ? 0 : countDays.value) * (form_data.value.date_dir === "before" ? -1 : 1)
            );
            d.setHours(8, 0, 0, 0);
            return formatSysDate(d);
        })

        const dueDate = computed(() => {
            if (props.pickerType === "workflow") {
                return null
            }

            if (!startDate.value) {
                return null
            }

            if (!form_data.value.due_count_days) {
                return startDate.value
            }

            const startDateParsed = parseSysDate(startDate.value)

            let d = new Date(startDateParsed.getTime())

            d = d.calcAddDays(form_data.value.due_count_days);
            d.setHours(23, 59, 59, 0);

            return formatSysDate(d);
        })

        const datesList = computed(() => [startDate.value, dueDate.value].filter(x => x))

        const getDatesColors = () => {
            let ret = { events: [], colors: {}};
            if (!form_data.value) {
                return ret;
            }

            if (!startDate.value || !dueDate.value) {
                return ret
            }

            if (startDate.value === dueDate.value) {
                let d = startDate.value.substr(0, 10);
                ret.events.push(d);
                ret.colors[d] = "date-range__date-in-range date-range__range-single blue lighten-3";
                return ret;
            }

            let from = parseSysDate(startDate.value.substr(0, 10));
            let until = parseSysDate(dueDate.value.substr(0, 10));
            if (until < from) {
                until = parseSysDate(startDate.value.substr(0, 10));
                from = parseSysDate(dueDate.value.substr(0, 10));
            }

            let current = from;
            while (current <= until) {
                let d = formatSysDate(current);
                ret.events.push(d);
                ret.colors[d] = "date-range__date-in-range blue lighten-3";
                current.setDate(current.getDate() + 1);
            }
            ret.colors[ret.events[0]] += " date-range__range-start";
            ret.colors[ret.events[ret.events.length - 1]] += " date-range__range-end";

            return ret
        }

        const coloring = computed(function() {
            const ret = getDatesColors()

            if (projectDate.value) {
                if (ret.colors[projectDate.value]) {
                    ret.colors[projectDate.value] += ' calendar-project-date'
                } else {
                    ret.colors[projectDate.value] = 'calendar-project-date'
                    ret.events.push(projectDate.value)
                }
            }

            return ret;
        });

        const onChangeDatePicked = (value) => {
            const selectedDate = parseSysDate(value[0])
            const parsedProjectDate = parseSysDate(projectDate.value)

            let dayDiff = Math.ceil((selectedDate - parsedProjectDate) / 86400000);
            if (parsedProjectDate > selectedDate) {
                dayDiff *= -1
                form_data.value.date_dir = "before"
            } else {
                form_data.value.date_dir = "after"
            }

            const weeks = Math.floor(dayDiff / 7)
            const days = dayDiff % 7

            form_data.value.days = days
            form_data.value.weeks = weeks
            form_data.value.days_f = days.toString()
            form_data.value.weeks_f = weeks.toString()
        }

        const getRelativeFormattedDate = (val) => {
            let ret = [];
            if (!val.date_field) return "";
            if (!props.task.project) return "";
            let date_val = props.task.project[val.date_field];
            if (!date_val) return "";
            let d = parseSysDate(date_val).calcAddDays(
                (!val.count_days ? 0 : val.count_days) * (val.date_dir === "before" ? -1 : 1)
            );
            d.setHours(8, 0, 0, 0);
            ret.push(d.formatDate());
            if (val.due_count_days) {
                d = d.calcAddDays(val.due_count_days);
                d.setHours(23, 59, 59, 0);
                ret.push(d.formatDate());
            }
            return ret.join(" / ");
        }

        const getFallbackDate = () => {
            if (!props.task) {
                return null
            }

            const state = props.task.recurrence_rule ? props.task.recurrence_rule : {
                type: "date",
                date: props.task.date,
                date_only: props.task.date_only,
                has_due_date: props.task.has_due_date,
                due_date: props.task.due_date,
                due_date_only: props.task.due_date_only,
                time: props.task.date_only ? "" : props.task.date.length >= 16 ? props.task.date.substr(11, 5) : "",
                due_time: props.task.due_date_only
                    ? ""
                    : props.task.due_date && props.task.due_date.length >= 16
                        ? props.task.due_date.substr(11, 5)
                        : ""
            };

            return formatRecurrence(state)
        }

        const formatOutput = (val) => {
            if (props.pickerType === 'workflow') {
                return formatRecurrenceWorkflow(val, custom_fields.value[props.task.account_id]);
            } else if (props.task && props.task.fields && props.task.fields.workflow_date_rule) {
                const date = getRelativeFormattedDate(val)
                if (date) {
                    return date
                } else {
                    return getFallbackDate()
                }
            }
        }
        const integer_field_changed = function(field, data) {
            form_data.value[field + "_error"] = null;

            if (!data) {
                form_data.value[field] = null;
                return;
            }

            let num_str = cleanNumber(data);
            if (isNaN(num_str)) {
                form_data.value[field + "_error"] = $t("Must be a number");
                form_data.value[field] = 0;
            } else {
                form_data.value[field] = parseInt(num_str);
            }
        };

        onMounted(() => {
            if (!(props.task.fields && props.task.fields.workflow_date_rule)) {
                load_data();
            }
        })

        watch(menu_open, (val) => {
            if (val) {
                opening.value = true;
                load_data();
            }
            else {
                form_data.value = null;
            }
        });

        watch(() => startDate.value, () => {
            if (startDate.value) {
                pickerDate.value = startDate.value
            }
        })

        return {
            menu_open,
            current_state,
            saveButton,
            form_data,
            workflow_settings,
            cancel,
            save,
            integer_field_changed,
            formatOutput,
            formatOutputHint,
            startDate,
            dueDate,
            locale,
            firstDayOfWeek,
            datesList,
            pickerDate,
            coloring,
            onChangeDatePicked,
            allowedDateFields,
            isEventTypeCompany
        };
    }
}
</script>

<style scoped lang="scss">
.task-form-elem {
    ::v-deep input, ::v-deep .v-select__selection {
        font-size: 14px !important;
        font-weight: 600 !important;
    }
}

::v-deep .calendar-project-date {
    &:after {
        position: absolute;
        content: " ";
        left: 0;
        top: 0;
        height: 40px;
        width: 40px;
        border: 1px solid var(--v-secondary-base);
        border-radius: 50%;
    }
}

::v-deep .date-picker-range .date-range__date-in-range.date-range__range-start::after {
    width: 40px !important;
}

.workflow-date-field-input ::v-deep .v-input__icon.v-input__icon--append svg {
    font-size: 16px;
    width: 16px;
    height: 16px;
}

</style>